import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

const CustomMultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  onMenuOpen,
  placeholder = "Select",
  isSearchable = true,
  isDisabled = false,
  isLoading = false,
  isOptionDisabled = () => false,
  className = "",
  id,
  key,
  menuPortalTarget,
  menuPosition = 'fixed',
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Initialize selected options from value prop
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedOptions(value.filter(item => item?.value && item?.label));
    }
  }, [value]);

  // Calculate menu position
  const calculateMenuPosition = () => {
    if (!triggerRef.current || !isOpen) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const menuHeight = 250; // Estimated max height

    let position = {
      top: triggerRect.bottom + window.scrollY,
      left: triggerRect.left + window.scrollX,
      width: triggerRect.width,
      minWidth: triggerRect.width
    };

    // If using fixed positioning, calculate based on viewport
    if (menuPosition === 'fixed') {
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        // Not enough space below, position above
        position = {
          top: triggerRect.top - menuHeight + window.scrollY,
          left: triggerRect.left + window.scrollX,
          width: triggerRect.width,
          minWidth: triggerRect.width,
          bottom: 'auto'
        };
      } else {
        // Position below
        position = {
          top: triggerRect.bottom + window.scrollY,
          left: triggerRect.left + window.scrollX,
          width: triggerRect.width,
          minWidth: triggerRect.width,
          bottom: 'auto'
        };
      }
    }

    return position;
  };

  // Update menu position when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      const position = calculateMenuPosition();
      setMenuStyle(position);

      // Update position on window resize
      const handleResize = () => {
        const newPosition = calculateMenuPosition();
        setMenuStyle(newPosition);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize, true);
      };
    }
  }, [isOpen, menuPosition]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];

    const filtered = options.filter(option =>
      !option.isDisabled &&
      (isSearchable ? option.label?.toLowerCase().includes(searchQuery.toLowerCase()) : true)
    );

    return filtered;
  }, [options, searchQuery, isSearchable]);

  // Check if an option is selected
  const isOptionSelected = (optionValue) => {
    return selectedOptions.some(item => item.value === optionValue);
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (isOptionDisabled(option) || option.isDisabled) return;

    let newSelectedOptions;

    if (isOptionSelected(option.value)) {
      // Remove option
      newSelectedOptions = selectedOptions.filter(item => item.value !== option.value);
    } else {
      // Add option
      newSelectedOptions = [...selectedOptions, option];
    }

    setSelectedOptions(newSelectedOptions);

    // Call onChange with the new selected options
    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  // Remove a selected option
  const handleRemoveOption = (optionValue, e) => {
    if (e) e.stopPropagation();

    const newSelectedOptions = selectedOptions.filter(item => item.value !== optionValue);
    setSelectedOptions(newSelectedOptions);

    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  // Clear all selected options
  const handleClearAll = (e) => {
    if (e) e.stopPropagation();

    setSelectedOptions([]);
    if (onChange) {
      onChange([]);
    }
  };

  // Handle dropdown toggle
  const handleToggleDropdown = () => {
    if (isDisabled) return;

    const newOpenState = !isOpen;
    setIsOpen(newOpenState);

    if (newOpenState && onMenuOpen) {
      onMenuOpen();
    }

    if (newOpenState && isSearchable) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchQuery('');
        break;
      default:
        break;
    }
  };

  // Component classes
  const containerClasses = `custom-multi-select-dropdown ${className}`;
  const triggerClasses = `custom-dropdown-trigger inside-border-inpt ${isDisabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`;

  return (
    <div
      ref={dropdownRef}
      className={containerClasses}
      style={{ position: 'relative', width: '100%', minWidth: 0, }}
      id={id}
      key={key}
    >
      {/* Dropdown Trigger */}
      <div
        ref={triggerRef}
        className={triggerClasses}
        style={{
          borderRadius: '8px',
          border: `1px solid ${isDisabled ? '#e9ecef' : isOpen ? '#80bdff' : '#ccc'}`,
          minHeight: '38px',
          padding: '2px 8px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '4px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isDisabled ? '#f8f9fa' : 'white',
          width: '100%',
          minWidth: 0,
          opacity: isDisabled ? 0.6 : 1,
          boxShadow: isOpen ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
        }}
        onClick={handleToggleDropdown}
        tabIndex={isDisabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={isDisabled}
      >
        {/* Selected Options */}
        {selectedOptions.length === 0 && !(isOpen && isSearchable) ? (
          <span
            style={{
              color: isDisabled ? '#adb5bd' : '#999',
              padding: '6px',
              flex: 1,
              fontSize: '14px'
            }}
          >
            {placeholder}
          </span>
        ) : (
          <>
            {selectedOptions.map((option) => (
              <div
                key={`selected-${option.value}`}
                className="selected-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#e6f3ff',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  margin: '2px',
                  minWidth: 'fit-content',
                  maxWidth: '200px',
                  flexShrink: 0
                }}
              >
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '180px',
                    padding: '0 4px',
                    fontSize: '14px'
                  }}
                  title={option.label}
                >
                  {option.label}
                </span>
                {(
                  <button
                    disabled={isDisabled}
                    type="button"
                    onClick={(e) => handleRemoveOption(option.value, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#666',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    aria-label={`Remove ${option.label}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            {/* Search Input when dropdown is open */}
            {isOpen && isSearchable && !isDisabled && (
              <input
                ref={searchInputRef}
                type="text"
                className="dropdown-search-input"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  border: 'none',
                  outline: 'none',
                  padding: '6px',
                  flex: 1,
                  minWidth: '60px',
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  minWidth: '80px'
                }}
                aria-label="Search options"
              />
            )}

            {/* Clear All Button */}
            {/* {!isDisabled && selectedOptions.length > 0 && !isOpen && (
              <button
                type="button"
                onClick={handleClearAll}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: '#dc3545',
                  fontSize: '12px',
                  marginLeft: 'auto',
                  flexShrink: 0
                }}
                aria-label="Clear all selections"
              >
                Clear
              </button>
            )} */}
          </>
        )}

        {/* Dropdown Indicator */}
        <div style={{ marginLeft: 'auto', padding: '0 4px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {/* {!isDisabled && selectedOptions.length > 0 && !isOpen && (
            <span 
              style={{ 
                color: '#666', 
                fontSize: '12px', 
                marginRight: '8px',
                fontWeight: 'normal',
                whiteSpace: 'nowrap'
              }}
            >
              {selectedOptions.length} selected
            </span>
          )} */}
          <span style={{ color: isDisabled ? '#adb5bd' : '#666', fontSize: '12px' }}>
            {isOpen ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && !isDisabled && (
        <div
          className="custom-dropdown-menu position-custom-select"
          style={{
            // position: menuPosition === 'fixed' ? 'fixed' : 'absolute',
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            marginTop: '4px',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 9999,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            ...menuStyle
          }}
          role="listbox"
          aria-multiselectable="true"
        >
          {/* Loading State */}
          {isLoading ? (
            <div
              style={{
                padding: '16px',
                color: '#666',
                textAlign: 'center',
                fontSize: '14px'
              }}
              aria-live="polite"
            >
              Loading...
            </div>
          ) : (
            <>
              {/* No options available */}
              {options.length === 0 ? (
                <div
                  style={{
                    padding: '16px',
                    color: '#666',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}
                >
                  No options available
                </div>
              ) : (
                <>
                  {/* Filtered Options */}
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => {
                      const optionDisabled = isOptionDisabled(option) || option.isDisabled;
                      const optionSelected = isOptionSelected(option.value);

                      return (
                        <div
                          key={`option-${option.value}`}
                          className={`dropdown-option ${optionSelected ? 'selected' : ''} ${optionDisabled ? 'disabled' : ''}`}
                          style={{
                            padding: '10px 12px',
                            cursor: optionDisabled ? 'not-allowed' : 'pointer',
                            backgroundColor: optionSelected ? '#e6f3ff' : 'transparent',
                            color: optionDisabled ? '#999' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontSize: '14px',
                            borderBottom: '1px solid #f1f1f1',
                            transition: 'background-color 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!optionDisabled) {
                              handleOptionSelect(option);
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (!optionDisabled) {
                              e.currentTarget.style.backgroundColor = optionSelected ? '#d4e7ff' : '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!optionDisabled) {
                              e.currentTarget.style.backgroundColor = optionSelected ? '#e6f3ff' : 'transparent';
                            }
                          }}
                          role="option"
                          aria-selected={optionSelected}
                          aria-disabled={optionDisabled}
                          title={option.label}
                        >
                          {/* Checkbox for selection */}
                          <div
                            style={{
                              flexShrink: 0,
                              width: '16px',
                              height: '16px',
                              border: `1px solid ${optionDisabled ? '#ddd' : optionSelected ? '#1890ff' : '#ccc'}`,
                              borderRadius: '3px',
                              backgroundColor: optionSelected ? '#1890ff' : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '10px'
                            }}
                          >
                            {optionSelected && '✓'}
                          </div>

                          {/* Option Label */}
                          <div
                            style={{
                              flex: 1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: optionDisabled ? '#999' : 'inherit'
                            }}
                          >
                            {option.label}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        padding: '16px',
                        color: '#666',
                        textAlign: 'center',
                        fontSize: '14px'
                      }}
                    >
                      No options found{searchQuery && ` for "${searchQuery}"`}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// PropTypes for type checking
CustomMultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool
  })),
  value: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })),
  onChange: PropTypes.func.isRequired,
  onMenuOpen: PropTypes.func,
  placeholder: PropTypes.string,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOptionDisabled: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  key: PropTypes.string,
  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(['absolute', 'fixed'])
};

CustomMultiSelectDropdown.defaultProps = {
  options: [],
  value: [],
  placeholder: "Select",
  isSearchable: true,
  isDisabled: false,
  isLoading: false,
  isOptionDisabled: () => false,
  className: "",
  menuPosition: 'fixed'
};

export default CustomMultiSelectDropdown;